/**
 * Frontend Admin Store - Auth State Management
 * =============================================================================
 */

import { create } from 'zustand';
import { authApi } from '../services/api';
import type { LoginResponse } from '../types';

// Auth user type (from login response)
type AuthUser = LoginResponse['user'];

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUserLanguage: (language: string) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  isLoading: true,

  login: async (email, password) => {
    const { data } = await authApi.login(email, password);
    const { token, user } = data;
    
    // Check if user is admin
    if (!user.isAdmin) {
      throw new Error('Admin access required');
    }
    
    localStorage.setItem('adminToken', token);
    
    // Store user's language preference
    if (user.language) {
      localStorage.setItem('happyeco-language', user.language);
    }
    
    set({ user, isAuthenticated: true, isAdmin: true, isLoading: false });
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    }
    localStorage.removeItem('adminToken');
    set({ user: null, isAuthenticated: false, isAdmin: false });
  },

  checkAuth: async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      set({ isLoading: false });
      return;
    }

    try {
      const { data: user } = await authApi.me();
      
      if (!user.isAdmin) {
        localStorage.removeItem('adminToken');
        set({ isLoading: false });
        return;
      }
      
      // Sync language preference from database
      if (user.language) {
        localStorage.setItem('happyeco-language', user.language);
      }
      
      set({ 
        user, 
        isAuthenticated: true, 
        isAdmin: true, 
        isLoading: false 
      });
    } catch {
      localStorage.removeItem('adminToken');
      set({ isLoading: false });
    }
  },

  setUserLanguage: (language: string) => {
    set((state) => ({
      user: state.user ? { ...state.user, language } : null,
    }));
  },
}));
